import { ChevronLeft, ChevronRight } from "lucide-react";
import type { OpenSlot } from "../api/demo-booking.js";
import { dateTileLabel, monthHeadingFor, timeLabel, type DateGroup } from "../lib/booking-dates.js";

interface BookingCalendarProps {
  /** The currently visible page of dates (already sliced by the caller) — never the full set. */
  dateGroups: readonly DateGroup[];
  selectedDateKey: string | null;
  selectedSlot: OpenSlot | null;
  canGoEarlier: boolean;
  canGoLater: boolean;
  onSelectDate: (dateKey: string) => void;
  onSelectSlot: (slot: OpenSlot) => void;
  onEarlier: () => void;
  onLater: () => void;
}

/**
 * The prototype's own calendar picker (GH-897), now wired to real server availability (GH-901)
 * instead of inventing a window client-side. "Earlier"/"Later" page through calendar dates that
 * actually have an open slot — both directions are now genuinely bounded by real availability,
 * not just "Earlier" as GH-898's synthetic version was.
 */
export function BookingCalendar({
  dateGroups,
  selectedDateKey,
  selectedSlot,
  canGoEarlier,
  canGoLater,
  onSelectDate,
  onSelectSlot,
  onEarlier,
  onLater,
}: BookingCalendarProps) {
  const selectedGroup = dateGroups.find((group) => group.dateKey === selectedDateKey) ?? null;

  return (
    <div className="booking-calendar">
      <div className="booking-calendar__head">
        <span className="booking-calendar__month">
          {monthHeadingFor(dateGroups.map((group) => group.date))}
        </span>
        <span className="booking-calendar__tz">Eastern Time</span>
      </div>
      <div className="booking-calendar__dates-row">
        <button
          type="button"
          className="booking-calendar__nav"
          aria-label="Earlier dates"
          disabled={!canGoEarlier}
          onClick={onEarlier}
        >
          <ChevronLeft size={16} />
        </button>
        <div className="booking-calendar__dates" role="group" aria-label="Choose a date">
          {dateGroups.map((group) => {
            const { weekday, day } = dateTileLabel(group.date);
            const isSelected = group.dateKey === selectedDateKey;
            return (
              <button
                key={group.dateKey}
                type="button"
                className="booking-calendar__date"
                aria-pressed={isSelected}
                onClick={() => onSelectDate(group.dateKey)}
              >
                <span>{weekday}</span>
                <b>{day}</b>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="booking-calendar__nav"
          aria-label="Later dates"
          disabled={!canGoLater}
          onClick={onLater}
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="booking-calendar__times" role="group" aria-label="Choose a time">
        {(selectedGroup?.slots ?? []).map((slot) => {
          const isSelected =
            selectedSlot !== null && selectedSlot.startsAt.getTime() === slot.startsAt.getTime();
          return (
            <button
              key={slot.startsAt.toISOString()}
              type="button"
              className="booking-calendar__time"
              aria-pressed={isSelected}
              onClick={() => onSelectSlot(slot)}
            >
              {timeLabel(slot)}
            </button>
          );
        })}
      </div>
      <p className="booking-calendar__note">
        Times shown are real openings — booking holds the slot immediately.
      </p>
    </div>
  );
}
